import { useCallback, useState, useEffect } from 'react';
import { debounce } from 'throttle-debounce';
import { useFormContext } from 'react-hook-form';
import Delta from 'quill-delta';
import { Range } from 'quill';
// Store
import { useAppSelector } from 'app/hooks/useStore';
// Selectors
import { selectMyUser } from 'app/store/Users/Users.selectors';

import { Box } from '@mui/material';

import { CommentsContext, IComment, IAnchor, CommentStatuses, CommentStatusTypes } from './useCommentsContext';
import { applyCommentFormatting, transformRange } from '../documentQuillUtils';

import AddCommentButton from './AddCommentButton';
import ToggleCommentsButton from './ToggleCommentsButton';
import CommentListItems from './CommentListItems';

type Props = {
  quillRef: React.RefObject<{ getQuill: () => any | null }>;
}

export default function Comments({ quillRef }: Props) {
  const myUser = useAppSelector(selectMyUser);

  const { setValue, watch } = useFormContext();

  const comments = watch('comments');

  const [ selectedCommentId, setSelectedCommentId ] = useState<number | null>(null);
  const [ showComments, setShowComments ] = useState(true);

  const quillFocus = useCallback(() => {
    const quill = quillRef.current?.getQuill();
    if ( quill ) quill.root.focus();
  }, [quillRef]);

  const handleAddComment = useCallback((content:string, anchor:IAnchor) => {
    const comment: IComment = {
      id: new Date().getTime(), // Temporary ID, real one should come from backend
      content,
      createdOn: new Date().toISOString(),
      modifiedOn: new Date().toISOString(),
      createdBy: {
        id: myUser?.id || 0,
        name: myUser?.name || 'Current User',
      },
      anchor,
      status: CommentStatuses.added
    };
    setValue('comments', [...comments, comment]);
    setSelectedCommentId(comment.id);
    setShowComments(true);

    quillFocus();
  }, [quillFocus, setValue, comments, myUser]);

  const handleUpdateComment = useCallback((commentId:number, content:string) => {
    setValue('comments', comments.map((comment:any) => {
      if ( comment.id !== commentId ) return comment;
      return { ...comment, content, status: CommentStatuses.none }
    }));
    quillFocus();
  }, [quillFocus, setValue, comments]);

  const handleRemoveComment = useCallback((comment:IComment) => {
    if ( comment.anchor ) applyCommentFormatting(quillRef, comment.anchor);
    setValue('comments', comments.filter((c:IComment) => c.id !== comment.id));
    quillFocus();
  }, [quillFocus, quillRef, setValue, comments]);

  const handleChangeCommentStatus = useCallback((commentId:number, status:CommentStatusTypes) => {
    setValue('comments', comments.map((comment:IComment) => {
      if ( comment.id !== commentId ) return comment;
      return { ...comment, status }
    }));
  }, [setValue, comments]);

  useEffect(() => {
    const quill = quillRef.current?.getQuill();

    if (!quill) return;

    let pendingDelta = new Delta();

    setTimeout(() => {
      // Apply formatting for existing comments
      for ( const comment of comments ) {
        if ( !comment.anchor ) continue;
        applyCommentFormatting(quillRef, comment.anchor, comment.id);
      }
    }, 500);

    const debouncedUpdateComments = debounce(500, () => {
      const updatedComments = comments
        .map((comment: IComment) => {
          if ( !comment.anchor ) return { ...comment };
          const newAnchor = transformRange(comment.anchor, pendingDelta, true);
          return newAnchor && newAnchor.l ? { ...comment, anchor: newAnchor } : null;
        })
        .filter(Boolean);
      setValue('comments', updatedComments);
      pendingDelta = new Delta();
    });

    // Helper: safely remove `comment` attribute on [index, length]
    const stripCommentAttr = (index: number, length: number) => {
      if (length <= 0) return;
      try {
        // 'silent' so we don't re-trigger text-change or add to history
        quill.formatText(index, length, 'comment', false, 'silent');
      } catch (err) {
        // ignore — sometimes index/length race may cause out-of-range errors
        console.warn('stripCommentAttr failed', err);
      }
    };

    const handleTextChange = (delta: Delta, oldDelta: Delta, source:string) => {
      if ( source === 'user' ){
        pendingDelta = pendingDelta.compose(delta);

        // We only need to strip comment attributes for user-triggered inserts/attribute changes
        if (Array.isArray(delta.ops)) {
          let index = 0; // current position in document while walking delta.ops

          for (const op of delta.ops) {
            if (op.retain) {
              // If the retain op comes with attribute changes (e.g. attributes.comment),
              // remove the comment attribute for that retained range.
              if (op.attributes && Object.prototype.hasOwnProperty.call(op.attributes, 'comment')) {
                stripCommentAttr(index, op.retain as number);
              }
              index += op.retain as number;
            } else if (op.insert) {
              // Remove comment attribute from newly inserted text/embeds
              const len = typeof op.insert === 'string' ? op.insert.length : 1;
              stripCommentAttr(index, len);
              index += len;
            } else if (op.delete) {
              // deleted content lives at current index and does not advance `index`.
              // We'll remove comments for deleted ranges in the debounced update (transformRange => null)
              // so nothing to do here.
            }
          }

          debouncedUpdateComments();
        }
      }
    }

    // Track cursor position to select/deselect comments
    const handleSelectionChange = (range: Range) => {
      if ( range && typeof range.index === 'number' && typeof range.length === 'number' && range.length === 0 ) {
        // Cursor position
        const format = quill.getFormat(range);
        if ( format && format.comment ){
          if ( format.comment === selectedCommentId ) return; // No change
          // Select comment
          setSelectedCommentId(format.comment || null);
        } else {
          setSelectedCommentId(null);
        }
      }
    }

    quill.on('text-change', handleTextChange);
    quill.on('selection-change', handleSelectionChange);
    return () => {
      quill.off('text-change', handleTextChange);
      quill.off('selection-change', handleSelectionChange);
      debouncedUpdateComments.cancel && debouncedUpdateComments.cancel();
    };
  }, [quillRef, comments, selectedCommentId, setValue]);

  useEffect(() => {
    const quill = quillRef.current?.getQuill();

    if (!quill) return;

    if ( selectedCommentId ){
      // remove old highlights
      quill.root.querySelectorAll('.ql-comment.is-active')
        .forEach((el: HTMLElement) => el.classList.remove('is-active'));

      // highlight the one under cursor
      quill.root.querySelectorAll(`[data-comment-id="${selectedCommentId}"]`)
        .forEach((el: HTMLElement) => el.classList.add('is-active'));
    } else {
      // remove old highlights
      quill.root.querySelectorAll('.ql-comment.is-active')
        .forEach((el: HTMLElement) => el.classList.remove('is-active'));
    }
  }, [quillRef, selectedCommentId]);

  return (
    <CommentsContext.Provider
      value={{
        quillRef,
        // State
        comments,
        selectedCommentId,
        setSelectedCommentId,
        // Methods
        showComments,
        onToggleComments: () => setShowComments(prev => !prev),
        onAddComment: handleAddComment,
        onUpdateComment: handleUpdateComment,
        onRemoveComment: handleRemoveComment,
        onChangeCommentStatus: handleChangeCommentStatus,
      }}
    >
      <Box sx={{
        width: '100%',
        maxWidth: !comments.length || !showComments ? 0 : 360, flexShrink: 0, position: 'relative' }}>
        <AddCommentButton />
        {comments.length ? <ToggleCommentsButton /> : null}
        {showComments ? (
          <CommentListItems />
        ) : null}
      </Box>
    </CommentsContext.Provider>
  )
}
